import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureEntitlements } from "@frontegg/react";
import Button from "@mui/material/Button";
import { PageHeader } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import AccountType from "enums/AccountType";
import ApplicationType from "enums/ApplicationType";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useGetBankAccounts from "hooks/useGetBankAccounts";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import FinancialAccountsDropdown from "components/common/FinancialAccountsDropdown";
import AccountActionsMenu from "components/admin/bank/AccountActionsMenu";
import BankBalanceCard from "components/admin/bank/BankBalanceCard";
import AccountDetailsCard from "components/admin/bank/AccountDetailsCard";
import BankActivityPanel from "components/admin/bank/BankActivityPanel";
import AccountDetailsDialog from "components/admin/bank/AccountDetailsDialog";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import type { AccountDrawer } from "components/admin/bank/AccountActionsMenu";

/**
 * One financial account at a time: its balance history, its details, and the
 * activity booked against it. The page owns the selected account and fetches
 * the account list once, so the cards below and the menu's drawers all read
 * the same result.
 */
const BankSettings: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Bank Settings"));

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [requestedDrawer, setRequestedDrawer] = useState<AccountDrawer | null>(
    null,
  );
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [wireOpen, setWireOpen] = useState(false);
  const [reviewTransactionId, setReviewTransactionId] = useState("");

  // getListBankAccounts is server-gated at Auditor. The nav entry is gated to
  // match, but a direct URL is not, so asking below that role would only earn a
  // rejection and an empty page.
  const isAuditorOrHigher = useCheckUserRole(Role.Auditor);

  const bankAccounts = useGetBankAccounts({
    accountTypes: [AccountType.FINANCIAL_ACCOUNT, AccountType.BANK],
    skip: !isAuditorOrHigher,
  });
  const { internalAccounts, isLoading, loadingBalances, refetchAllAccounts } =
    bankAccounts;

  // Derived rather than waited for. The seeding effect below runs after the
  // first paint, and an empty id reads as "every account" to the children, so
  // relying on it alone shows one frame of the whole company's ledger.
  // Validated, not just defaulted: a refetch can remove the selected account
  // (deactivated, unlinked), and a stored id that no longer matches anything
  // would leave the picker blank while the queries stayed scoped to a ghost.
  const selectionExists = internalAccounts.some(
    (account) => account.id === selectedAccountId,
  );
  const effectiveAccountId = selectionExists
    ? selectedAccountId
    : (internalAccounts[0]?.id ?? "");
  const selectedAccount = internalAccounts.find(
    (account) => account.id === effectiveAccountId,
  );

  // A transfer moves the balance as well as the ledger, and the two come from
  // different queries: the tables read refreshIndex, the balance and its curve
  // do not, so without both halves the page kept showing the pre-transfer
  // figure until the account changed or the page reloaded.
  // The menu lists different actions for a charge programme and defaults to
  // prepaid. AccountsPanel derives this the same way.
  const isChargeAccount =
    selectedAccount?.applicationType === ApplicationType.CHARGE;

  const refresh = () => {
    setRefreshIndex((index) => index + 1);
    refetchAllAccounts();
  };

  // AccountActionsMenu hides its Auto Balance item without this, so a second
  // way in has to honour it too.
  const { isEntitled: canManageBanks } = useFeatureEntitlements(
    "feature_manage_banks",
  );

  // Land on the first account rather than on nothing, and only while the page
  // has no selection of its own to keep.
  useEffect(() => {
    if (!selectedAccountId && internalAccounts.length) {
      setSelectedAccountId(internalAccounts[0].id);
    }
  }, [internalAccounts, selectedAccountId]);

  return (
    <Stack gap="var(--space-5)" sx={{ padding: "var(--space-6)" }}>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={t("Bank Settings")}
        subtitle={t("BankSettingsSubtitle")}
        actions={
          // The header sizes its action slot to content, and a flex child that
          // may shrink lands narrower than its own label.
          <Inline
            gap="var(--space-3)"
            wrap={false}
            sx={{ "& > *": { flexShrink: 0 } }}
          >
            <FinancialAccountsDropdown
              value={effectiveAccountId}
              onChange={(account) => setSelectedAccountId(account?.id ?? "")}
              includeAllOption={false}
              showTitle={false}
              width={200}
              // The page's own fetch, narrowed to what this picker lists, so
              // there is one account request on the page rather than two.
              accounts={{ ...bankAccounts, allAccounts: internalAccounts }}
            />
            {/* The entitlement is not the role gate: skipping the queries
                left this button, and the drawer behind it, reachable on a
                direct URL below Auditor. */}
            {canManageBanks && isAuditorOrHigher && (
              <Button
                startIcon={<HugeiconsIcon icon={PlusSignIcon} size={16} />}
                onClick={() => setRequestedDrawer("autoBalance")}
              >
                {t("Balance rules")}
              </Button>
            )}
            {/* The drawers only: this page opens Auto Balance from its own
                button, and the prototype carries no overflow menu. */}
            {/* Not just its button: the menu mounts AutoBalanceDrawer, which
                runs GET_BALANCE_SETTINGS on mount rather than on open, and that
                query is held at Auditor, and the drawer is entitlement-gated
                everywhere else, so the mount needs both. */}
            {isAuditorOrHigher && canManageBanks && (
              <AccountActionsMenu
                hideTrigger
                selectedBankAccountId={effectiveAccountId}
                charge={isChargeAccount}
                requested={requestedDrawer}
                onRequestedClose={() => setRequestedDrawer(null)}
                refreshTransactions={refresh}
                bankAccounts={bankAccounts}
              />
            )}
          </Inline>
        }
      />

      <Columns gap="var(--space-4)" count={{ xs: 1, lg: "7fr 5fr" }}>
        <BankBalanceCard
          key={refreshIndex}
          account={selectedAccount}
          // availableAmount comes from the separate balances query, so the
          // metadata flag alone would show a pre-transfer figure as settled.
          loading={isLoading || loadingBalances}
        />

        <AccountDetailsCard
          account={selectedAccount}
          onWireInstructions={() => setWireOpen(true)}
        />
      </Columns>

      <AccountDetailsDialog
        open={wireOpen}
        account={selectedAccount}
        onClose={() => setWireOpen(false)}
      />

      <BankActivityPanel
        accountId={effectiveAccountId}
        loading={isLoading}
        refreshIndex={refreshIndex}
        onRowClick={setReviewTransactionId}
      />

      <TransactionDetailsDrawer
        // The drawer reads its own transaction, so an id is all it needs, and
        // an empty one is what closed looks like.
        open={Boolean(reviewTransactionId)}
        transactionId={reviewTransactionId}
        handleCloseDrawer={() => setReviewTransactionId("")}
        handleRefreshTransactions={refresh}
      />
    </Stack>
  );
};

export default BankSettings;
